import { useState, useMemo, useRef, useEffect } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils/cn';

/** Minúsculas y sin acentos: "cred" tiene que encontrar "Créditos". */
const normalizar = (texto: string) =>
  texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

/**
 * Filtro del buscador. Exportado para testearlo sin montar el componente.
 * Sin tildes ni mayúsculas y por PALABRAS: cada palabra tiene que estar, en cualquier orden, en el rótulo o en
 * las `keywords` de la opción (por ejemplo el NIT o el correo de un cliente, que no se muestran).
 */
export function filtrarOpciones<T extends { label: string; keywords?: string }>(
  options: T[],
  search: string
): T[] {
  const palabras = normalizar(search).split(/\s+/).filter(Boolean);
  if (palabras.length === 0) return options;
  return options.filter((o) => {
    const texto = normalizar(`${o.label} ${o.keywords ?? ''}`);
    return palabras.every((p) => texto.includes(p));
  });
}

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Texto que también se busca pero no se muestra (NIT, correo…). */
  keywords?: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  /**
   * Poner en true cuando el select vive dentro de un Dialog: el scroll-lock
   * del diálogo bloquea la rueda sobre un popover no-modal (portaleado fuera
   * del DialogContent) y la lista de opciones no scrollea.
   */
  modal?: boolean;
  /**
   * Avisa cada cambio del texto buscado (y `''` al cerrar). Sirve para que el
   * padre consulte el servidor cuando la lista cargada no está completa —p.ej.
   * los clientes del Pricer, que son más de los 5.000 que se traen de entrada—.
   * El filtrado local sigue igual.
   */
  onSearchChange?: (search: string) => void;
  /** Mientras el padre busca en el servidor: «Buscando...» en vez del vacío. */
  loading?: boolean;
  /** Para que un `<label htmlFor>` le dé nombre al botón. */
  id?: string;
  /** Nombre accesible cuando no hay un `<label>` asociado. */
  'aria-label'?: string;
}

export function SearchableSelect({
  options,
  value,
  onValueChange,
  placeholder = 'Seleccionar...',
  searchPlaceholder = 'Buscar...',
  emptyMessage = 'Sin resultados.',
  className,
  disabled,
  modal = false,
  onSearchChange,
  loading = false,
  id,
  'aria-label': ariaLabel,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => filtrarOpciones(options, search), [options, search]);

  const cambiarBusqueda = (texto: string) => {
    setSearch(texto);
    onSearchChange?.(texto);
  };

  const cambiarApertura = (abierto: boolean) => {
    setOpen(abierto);
    if (!abierto) cambiarBusqueda('');
  };

  const selectedLabel = options.find((o) => o.value === value)?.label;

  // Focus search input when popover opens
  useEffect(() => {
    if (open) {
      // Small delay to let the popover render
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    } else {
      setSearch('');
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={cambiarApertura} modal={modal}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          id={id}
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          className={cn(
            'flex h-10 w-full min-w-0 items-center justify-between overflow-hidden rounded-md border border-input bg-campo px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            !selectedLabel && 'text-muted-foreground',
            className
          )}
        >
          <span className="truncate">{selectedLabel || placeholder}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="min-w-[--radix-popover-trigger-width] w-auto max-w-[24rem] p-0"
        align="start"
      >
        {/* Search input */}
        <div className="flex items-center border-b px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => cambiarBusqueda(e.target.value)}
            placeholder={searchPlaceholder}
            className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>

        {/* Options list */}
        {/* `listbox`/`option` no son decorativos: un combobox sin ellos no se
            puede navegar con lector de pantalla ni localizar por rol. */}
        <div className="max-h-60 overflow-y-auto p-1" role="listbox">
          {filtered.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {loading ? 'Buscando...' : emptyMessage}
            </p>
          ) : (
            /*
              La key lleva el índice porque `value` puede repetirse (en el
              Pricer es el nombre del cliente, y hay cientos iguales). Con keys
              duplicadas React no borra las filas viejas al filtrar y la lista
              parece no reaccionar a lo que se escribe.
            */
            filtered.map((option, index) => (
              <button
                key={`${index}:${option.value}`}
                type="button"
                role="option"
                aria-selected={value === option.value}
                onClick={() => {
                  onValueChange(option.value);
                  cambiarApertura(false);
                }}
                className={cn(
                  'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                  value === option.value && 'bg-accent'
                )}
              >
                <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                  {value === option.value && <Check className="h-4 w-4" />}
                </span>
                <span className="whitespace-nowrap text-left">{option.label}</span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
